import debounce from 'lodash/debounce';
import get from 'lodash/get';
import flatMap from 'lodash/flatMap';
import isNumber from 'lodash/isNumber';
import throttle from 'lodash/throttle';
import {Component} from 'panel';

import {TooltipItem, TooltipItemType} from 'common/components/chart-tooltip/types';
import {abbreviateNumber, abbreviateNumber3SigFigs, commaizeNumber} from 'common/util';
import * as colors from 'common/util/colors';
import {registerMPElement} from 'common/util/register-element';
import {Alert, FunnelsAnomaly} from 'common/widgets/alerts/smart-hub/models';

import {
  BarType,
  ChartableStep,
  ClosePopoverEvent,
  ContextMenuStepEvent,
  CountType,
  SelectBarEvent,
  SelectColumnEvent,
  SelectedBar,
  Step,
  UpdateStartStepEvent,
} from './types';
import {barHeight, chartableSteps, getYInterval} from './utils';

import template from './index.jade';
import css from './index.styl?inline';

interface ChartState {
  steps?: ChartableStep[];
  smartHubDataPoint: any;
  hoverTooltip?: any;
}

const CHART_LABEL_HEIGHT = 90;
const CHART_BAR_HEIGHT = 260;
const MULTI_BAR_CHART_COUNT_HEIGHT = 50;
const SINGLE_BAR_CHART_COUNT_HEIGHT = 90;

export class FunnelsChart extends Component<ChartState> {
  private lastStepUserCount: number;
  private maxOverallStepValue: number;
  /** how many bars are being shown per column */
  private barsPerColumn: number;

  static get attrsSchema() {
    return {
      colors: {type: `json`, default: []},
      count: {type: `string`, enum: [CountType.RELATIVE, CountType.ABSOLUTE], default: CountType.ABSOLUTE},
      'disable-context-menu': {type: `boolean`, description: `disable special right click actions`},
      'disable-drop-off-click': {type: `boolean`, description: `disable click on the drop-off bars`},
      'disable-drop-off-viz': {type: `boolean`, description: `don't show drop-off bars on each step`},
      'disable-header-click': {type: `boolean`, description: `disable clicking the header to select a step`},
      'disable-step-click': {type: `boolean`, description: `disable click on the conversion bars`},
      'disable-tooltip': {type: `boolean`, description: `don't show tooltip when there is popover`, default: false},
      'empty-message': `string`,
      'end-date': {type: `number`, description: `utc timestamp in milliseconds`},
      error: `string`,
      'funnels-count-type': {type: `string`, enum: [`unique`, `general`, `session`]},
      'has-overall-data': {type: `boolean`, description: `if false, show the empty-message`},
      'highlighted-steps': {type: `json`, description: `interface: HighlightedSteps`, default: {indices: []}},
      loading: {type: `boolean`, description: `if loading, show a spinner`},
      'placeholder-message': `string`,
      'project-id': `number`,
      'selected-bar': {type: `json`, description: `interface: SelectedBar - index of selected bar type`, default: {}},
      'selected-column': {type: `number`, description: `the index of the selected column`, default: null},
      'show-conversion-rate': {type: `boolean`, description: `show an overall conversion rate on the chart`},
      'smart-hub-alert': `json`,
      'start-date': {type: `number`, description: `utc timestamp in milliseconds`},
      'start-step': {type: `number`, description: `index of first step to be displayed`},
      'utc-offset': `number`,
      'visible-steps': {type: `number`, description: `number of steps to be displayed`},
      'workspace-id': `number`,
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        smartHubDataPoint: null,
        hoverTooltip: null,
      },
      helpers: {
        BarType,
        CHART_BAR_HEIGHT,
        abbreviateNumber,
        abbreviateNumber3SigFigs,
        commaizeNumber,
        handleMouseMove: throttle(
          (stepIdx, barIdx, barType, ev) => {
            const barElement = ev.target;
            const {width: curBarWidth, height: curBarHeight, left: curBarLeft} = barElement.getBoundingClientRect();
            // if the bar is not high enough, aligning the tooltip position from the bottom instead of top
            const TOOLTIP_HEIGHT_THRESHOLD = 100;
            const shouldPlaceBottom =
              (barType === BarType.CONVERTED && curBarHeight < TOOLTIP_HEIGHT_THRESHOLD) ||
              (barType === BarType.DROP_OFF &&
                !!barElement.nextSibling &&
                curBarHeight + barElement.nextSibling.getBoundingClientRect().height < TOOLTIP_HEIGHT_THRESHOLD);
            // if the space on the right is not enough, aligning the tooltip position from the right instead of left.
            const viewerRectRight = this.getBoundingClientRect().right;
            const TOOLTIP_WIDTH_THRESHOLD = 400;
            const shouldPlaceRight = viewerRectRight - curBarLeft < TOOLTIP_WIDTH_THRESHOLD;

            const hoverTooltip = {
              stepIdx,
              barIdx,
              barType,
              styles: {
                ...(shouldPlaceRight ? {right: `${curBarWidth + 10}px`} : {left: `${curBarWidth + 10}px`}),
                ...(shouldPlaceBottom ? {bottom: `0px`} : {top: `0px`}),
              },
            };
            this.update({hoverTooltip});
          },
          50,
          {leading: true, trailing: false},
        ),
        handleMouseLeave: () => this.update({hoverTooltip: null}),
        shouldShowTooltip: (stepIdx, barIdx, barType) => {
          const {hoverTooltip} = this.state;
          return (
            !this.attr(`disable-tooltip`) &&
            hoverTooltip &&
            hoverTooltip.stepIdx === stepIdx &&
            hoverTooltip.barIdx === barIdx &&
            hoverTooltip.barType === barType
          );
        },
        getTooltipData: (stepIdx, barIdx, bar) => {
          const {value: currentStepCount, conversionRate} = bar;
          let dropOffCount = null;
          if (stepIdx > 0) {
            const lastStepCount = this.state.steps[stepIdx - 1].data[barIdx].value;
            dropOffCount = commaizeNumber(lastStepCount - currentStepCount);
          }
          const countType = this.attr(`funnels-count-type`) === `unique` ? `uniques` : `total times`;
          return {
            conversionPercentage: abbreviateNumber(conversionRate * 100),
            conversionCount: commaizeNumber(currentStepCount),
            countType,
            dropOffCount,
            dropOffPercentage: abbreviateNumber((1 - conversionRate) * 100),
            title: bar.title,
          };
        },
        getUserConversionRate: () => this.helpers.formatPercent(this.lastStepUserCount / this.maxOverallStepValue),
        getUserCount: () => commaizeNumber(this.lastStepUserCount),
        hasConversionToNext: (step) => {
          // conversion arrow is only shown if single funnel
          return step.data.length === 1 && isNumber(this.helpers.firstBarInStep(step).conversionToNext);
        },

        isUnselected: (stepIndex) => {
          const selectedColumn = this.attr(`selected-column`);
          if (isNumber(selectedColumn)) {
            return stepIndex !== selectedColumn;
          } else {
            return false;
          }
        },

        isSelected: (stepIndex: number, barIndex: number, stepType: BarType) => {
          const bar: SelectedBar = this.attr(`selected-bar`);
          return stepIndex === bar.index && barIndex === bar.barIdx && stepType === bar.type;
        },

        isHighlighted: (stepIndex: number, barIdx: number, stepType: BarType) => {
          const {indices, type} = this.getHighlightedSteps();
          return indices.some((i) => i.index === stepIndex && i.barIdx === barIdx) && stepType === type;
        },

        unselectBarAndColumn: () => {
          if (!this.state.smartHubDataPoint) {
            this.unselectBar();
          }
          this.unselectColumn();
        },

        hasDropOff: (index, barIdx, step) => {
          if (index === 0) {
            return false;
          }

          const prevStep = this.overallSteps[index - 1].data[barIdx];
          return step.data[barIdx].value !== prevStep.value;
        },

        selectBar: (selectedStepIndex, selectedBarIndex, selectedBarType) => (ev) => {
          ev.stopPropagation();
          const {index, barIdx, type} = this.attr(`selected-bar`);
          if (selectedStepIndex === index && selectedBarIndex === barIdx && selectedBarType === type) {
            this.unselectBar();
          } else {
            this.selectBar(selectedStepIndex, selectedBarIndex, selectedBarType, ev);
          }
        },

        selectColumn: (idx) => {
          const selectedColumn = this.attr(`selected-column`);
          if (idx === selectedColumn) {
            this.unselectColumn();
          } else {
            this.selectColumn(idx);
          }
        },

        formatPercent: (decimal, precision = 1, appendPct = true) => {
          const pct = Math.round(decimal * Math.pow(10, precision + 2)) / Math.pow(10, precision);
          return appendPct ? pct + `%` : pct;
        },

        getSlopeColor: () => {
          return {
            startColor: `#ecf3f8`, // TODO: move to mixpanel-common stylus variable
            stopColor: `#ffffff`,
          };
        },

        getYAxisLabels: (steps) => {
          if (!steps || steps.length === 0 || steps.every((step) => step.value === 0)) {
            // @ts-ignore
            return;
          }

          if (this.attr(`count`) === CountType.RELATIVE) {
            return [
              {text: `25%`, y: 0.25},
              {text: `50%`, y: 0.5},
              {text: `75%`, y: 0.75},
              {text: `100%`, y: 1},
            ];
          } else {
            const yInterval = getYInterval(this.maxOverallStepValue);
            const yLabelCount = Math.min(Math.floor(this.maxOverallStepValue / yInterval), 6);
            return Array(yLabelCount)
              .fill(null)
              .map((_, idx) => {
                const value = (idx + 1) * yInterval;
                return {
                  text: abbreviateNumber(value),
                  y: value / this.maxOverallStepValue, // ratio from 0 - 1
                };
              });
          }
        },

        getVariableColumnStyles: () => {
          const columnGap = this.hasMultipleSeries ? 5 : 0;
          return {
            'grid-template-columns': (() => {
              const {steps} = this.state;
              const visibleSteps = this.attr(`visible-steps`);
              const spaceWithoutGaps = 100 - columnGap * (visibleSteps - 1);
              const repeatPercent = Math.ceil(spaceWithoutGaps / visibleSteps);

              return steps.length >= visibleSteps
                ? `repeat(${steps.length}, ${repeatPercent}%)`
                : `repeat(auto-fit, minmax(200px, 1fr))`;
            })(),
            'grid-column-gap': `${columnGap}%`,
          };
        },

        getTemplateRowStyle: () => {
          const countHeight = this.hasMultipleSeries ? MULTI_BAR_CHART_COUNT_HEIGHT : SINGLE_BAR_CHART_COUNT_HEIGHT;
          return `${CHART_LABEL_HEIGHT}px ${countHeight}px ${CHART_BAR_HEIGHT}px`;
        },

        getChartHeightStyle: () => {
          const countHeight = this.hasMultipleSeries ? MULTI_BAR_CHART_COUNT_HEIGHT : SINGLE_BAR_CHART_COUNT_HEIGHT;
          return `${CHART_LABEL_HEIGHT + countHeight + CHART_BAR_HEIGHT}px`;
        },

        handleScroll: debounce(() => {
          this.updateIndices(this.getStartIndex());
          if (this.attr(`selected-bar`)) {
            this.closePopover();
          }
        }, 10),

        trackScroll: throttle((ev) => {
          const startIndex = this.getStartIndex();
          this.el.dispatchEvent(
            new CustomEvent(`track`, {
              bubbles: true,
              composed: true,
              detail: {
                eventName: `[Funnels] Scroll chart steps`,
                properties: {
                  startIndex,
                  direction: ev.deltaX > 0 ? `right` : `left`,
                },
              },
            }),
          );
        }, 500),

        indexIsVisible: (idx) => {
          const startStep = this.attr(`start-step`);
          return idx >= startStep && idx < startStep + this.attr(`visible-steps`);
        },

        clickSmartHubDataPoint: (index, alert) => (ev) => {
          ev.stopPropagation(); // so we don't trigger clearEventIndex
          this.selectColumn(index + 1);
          const chartRect = this.getBoundingClientRect();
          const placeholderRect = ev.currentTarget.getBoundingClientRect();
          const top = placeholderRect.top - chartRect.top;
          const left = placeholderRect.left - chartRect.left;
          this.update({smartHubDataPoint: {alert, index, left, top}});
        },

        getAlertForStep: (stepIndex) => {
          const startDate = this.attr(`start-date`);
          const endDate = this.attr(`end-date`);

          if (!this.smartHubAlert || !startDate || !endDate) {
            return;
          }

          let alert = this.smartHubAlert.findAnomalyForStepIndex(stepIndex, startDate, endDate);
          if (alert) {
            // @ts-ignore
            const dataPoint = (this.dailySteps || []).find((step) => {
              return this.smartHubAlert.projectTimestamp(this.attr(`utc-offset`)) === step.date.getTime();
            });
            if (dataPoint) {
              alert = this.smartHubAlert.injectAnomalyValue(dataPoint.steps[stepIndex].step_conv_ratio);
            } else {
              // TODO(ML-840):
              // There's a small possibility that the request for daily conversion rate hasn't completed yet.
              console.error(`Daily conversion rate data for step not available`);
            }
          }
          return alert;
        },

        smartHubAlertPlacement: (stepIndex) => {
          const midStep = (this.attr(`start-step`) + this.attr(`visible-steps`) - 1) / 2;
          return stepIndex > midStep ? `left` : `right`;
        },

        conversionArrowStyles: (firstBarInStep, stepIdx) => {
          const barIdx = 0;
          const startY = barHeight(
            firstBarInStep.value,
            stepIdx,
            barIdx,
            this.overallSteps,
            this.attr(`count`),
            this.maxOverallStepValue,
          );
          const endY = barHeight(
            firstBarInStep.value * firstBarInStep.conversionToNext,
            stepIdx,
            barIdx,
            this.overallSteps,
            this.attr(`count`),
            this.maxOverallStepValue,
          );
          const bottom = Math.max(startY - (startY - endY) / 2, 8);

          return {bottom: `${bottom}%`};
        },

        contextMenu: (ev, step) => {
          if (this.attr(`disable-context-menu`)) {
            return;
          }

          ev.preventDefault();
          ev.stopPropagation();
          this.dispatchEvent(
            new CustomEvent(`contextMenuStep`, {
              detail: {
                step,
                mouseEvent: ev,
              },
              bubbles: true,
              composed: true,
            }) as ContextMenuStepEvent,
          );
        },

        smartHubAlertChange: (ev) => {
          if (ev.detail.state === `closed`) {
            this.update({smartHubDataPoint: null});
          }
        },

        getColor: (barIdx, type) => {
          const overrideColors = this.attr(`colors`);
          if (overrideColors && overrideColors.length > 0) {
            return overrideColors[barIdx];
          } else {
            if (type === BarType.CONVERTED) {
              if (this.hasMultipleSeries) {
                return colors.getChartSegmentColorCombo(barIdx).primary;
              } else {
                return colors.getChartSegmentColorCombo(0).primary;
              }
            } else {
              if (this.hasMultipleSeries) {
                return colors.getChartSegmentColorCombo(barIdx).primary;
              } else {
                return colors.mpColors[`$gray3`];
              }
            }
          }
        },
        countHeader: (step) => {
          return commaizeNumber(this.helpers.firstBarInStep(step).value);
        },
        firstBarInStep: (step) => {
          return step.data[0];
        },

        getTooltipSections: (title, percentage, count, countType, type): TooltipItem[][] => {
          return [
            [{text: title, type: TooltipItemType.Strong}],
            [
              {text: `${percentage}%`, type: TooltipItemType.Data},
              {text: type, type: TooltipItemType.Text},
            ],
            [
              {text: count, type: TooltipItemType.Data},
              {text: countType, type: TooltipItemType.Text},
            ],
          ];
        },
      },
    };
  }

  /**
   * When a user scrolls natively, we cannot detect which item is now the startStep,
   * so the workaround is to calculate the width of one of the steps.
   */
  private getStartIndex() {
    const columns = this.el.querySelector(`.columns`);
    const column = columns.children[0] as HTMLElement;
    // take column gap into account with offsetLeft
    const stepWidth = column.offsetWidth + column.offsetLeft;
    return Math.round(columns.scrollLeft / stepWidth);
  }

  private unselectBar() {
    const {type} = this.attr(`selected-bar`);
    this.selectBar(null, null, type);
  }

  private selectBar(index, barIdx, type, mouseEvent?) {
    this.unselectColumn();
    this.el.dispatchEvent(
      new CustomEvent(`selectBar`, {
        detail: {
          index,
          barIdx,
          type,
          mouseEvent,
        },
        bubbles: true,
        composed: true,
      }) as SelectBarEvent,
    );
  }

  private unselectColumn() {
    if (isNumber(this.attr(`selected-column`))) {
      this.selectColumn(null);
    }
  }

  private selectColumn(index) {
    this.el.dispatchEvent(
      new CustomEvent(`selectColumn`, {
        detail: {
          index,
        },
        bubbles: true,
        composed: true,
      }) as SelectColumnEvent,
    );
    if (isNumber(index) && !this.helpers.indexIsVisible(index)) {
      this.updateIndices(this.attr(`start-step`) + 1);
    }
  }

  private closePopover() {
    this.el.dispatchEvent(
      new CustomEvent(`closePopover`, {
        bubbles: true,
        composed: true,
      }) as ClosePopoverEvent,
    );
  }

  private updateIndices(startStep) {
    this.el.dispatchEvent(
      new CustomEvent(`updateStartStep`, {
        detail: {
          index: startStep,
        },
        bubbles: true,
        composed: true,
      }) as UpdateStartStepEvent,
    );
  }

  set overallSteps(steps: Step[]) {
    if (Array.isArray(steps)) {
      this.maxOverallStepValue = Math.max.apply(
        null,
        flatMap(steps, (step) => {
          return step.data.map((data) => data.value);
        }),
      );

      const _steps = chartableSteps(steps, this.attr(`count`), this.maxOverallStepValue);
      this.barsPerColumn = get(_steps, `[0].data.length`, 0);
      const lastStep = _steps.filter((step) => !step.empty).slice(-1)[0];
      this.lastStepUserCount = lastStep ? this.helpers.firstBarInStep(lastStep).value : 0;
      this.update({steps: _steps});
    } else {
      this.update({steps: null});
    }
  }
  get overallSteps() {
    return this.state.steps;
  }
  get hasMultipleSeries() {
    return this.barsPerColumn > 1;
  }

  /**
   * TODO: refactor this so all methods, getters etc. related
   * to smartHub are encapsulated in a class that can just
   * be invoked in getAlertForStep
   */
  get smartHubAlert(): FunnelsAnomaly | null {
    const smartHubAlert = this.attr(`smart-hub-alert`);
    const projectId = this.attr(`project-id`);

    if (smartHubAlert && projectId) {
      return Alert.create(smartHubAlert, projectId) as FunnelsAnomaly;
    }

    return null;
  }

  private getHighlightedSteps(): {indices: Array<{index: number; barIdx: number}>; type: BarType} {
    return this.attr(`highlighted-steps`);
  }
}

export default registerMPElement(`mp-funnels-chart`, FunnelsChart);
